import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { credentials, request, sendPart, SIZE_LIMIT, hash, uuid, bytes } from './api';
import { getAuthToken } from '../auth';
import { store } from './uploadStore';
import { hashFile } from './fileHash';
import { waitUpload } from '../utils/surveyorUpload';
import './storage.css';
const QueueContext = createContext(null);
export const useStorageUploads = () => useContext(QueueContext);
const scopeFor = ctx => hash(new TextEncoder().encode(ctx.share ? `share:${ctx.share}:${ctx.auth}` : `staff:${ctx.auth}`));

export default function UploadProvider({ children, publicToken = '' }) {
  const jobs = useRef([]), active = useRef(new Set()), alive = useRef(true), running = useRef(false);
  const [items, setItems] = useState([]), [expanded, setExpanded] = useState(true), [warning, setWarning] = useState(''), [hidden, setHidden] = useState(false);
  const emit = () => { if (alive.current) setItems(jobs.current.map(j => ({...j}))); };
  const persist = async job => {
    const { ctx, controller, ...saved } = job;
    try { await store('put', saved); } catch { if (alive.current) setWarning('Очередь работает в этой вкладке. Браузер не дал сохранить файлы: после перезагрузки их понадобится выбрать заново.'); }
  };
  const sameAccount = job => !job.ctx.auth || getAuthToken() === job.ctx.auth;
  async function withRetry(fn, job) {
    for (let attempt = 0; ; attempt++) {
      if (job.controller.signal.aborted) throw new Error('Загрузка приостановлена');
      if (!sameAccount(job)) throw new Error('Аккаунт изменился. Вернитесь в исходный аккаунт.');
      try { return await fn(); } catch (error) {
        if (attempt >= 6 || job.controller.signal.aborted || (error.status && error.status < 500 && ![408, 425, 429].includes(error.status))) throw error;
        await waitUpload(Math.min(1000 * 2 ** attempt, 10000), job.controller.signal);
      }
    }
  }
  async function work(job) {
    active.current.add(job.id); job.controller = new AbortController(); job.status = 'uploading'; job.error = ''; emit();
    try {
      if (!sameAccount(job)) throw new Error('Аккаунт изменился');
      job.sha256 = job.sha256 || await hashFile(job.file, {signal: job.controller.signal,
        onProgress: percent => { job.preparing = percent; emit(); }});
      job.preparing = null;
      await persist(job);
      const ctx = job.ctx, signal = job.controller.signal;
      const session = await withRetry(() => request('uploads/', {method:'POST', ctx, signal, data:{request_id:job.id, folder:job.folder, name:job.file.name, size:job.file.size, sha256:job.sha256, target:job.target?.id, revision:job.target?.revision}}),job);
      job.uploadId = session.id; await persist(job);
      if (session.status !== 'done') {
        if (!Number.isSafeInteger(session.chunk_size) || session.chunk_size <= 0 || !Array.isArray(session.received)) {
          throw new Error('Сервер не подтвердил размер частей загрузки. Повторите попытку.');
        }
        const count = Math.ceil(job.file.size / session.chunk_size), received = new Set(session.received);
        for (let index = 0; index < count; index++) {
          if (received.has(index)) continue;
          const part = job.file.slice(index * session.chunk_size, (index + 1) * session.chunk_size);
          await withRetry(() => sendPart(session.id, index, part, ctx, fraction => {
            job.progress = Math.min(99, Math.round((received.size + fraction) / count * 100)); emit();
          }, signal), job);
          received.add(index); job.progress = Math.min(99, Math.round(received.size / count * 100)); emit();
        }
      }
      const result = await withRetry(() => request(`uploads/${session.id}/finish/`, {method:'POST',ctx,signal,data:{}}),job);
      job.status = 'done'; job.progress = 100; job.file = {name:job.file.name,size:job.file.size};
      await store('delete', job.id).catch(() => {});
      window.dispatchEvent(new CustomEvent('storage:changed', {detail:{folder:job.folder, node:result.id}}));
    } catch (error) {
      job.status = job.controller.signal.aborted ? 'paused' : 'error'; job.error = error.message; if (alive.current) setHidden(false); await persist(job);
    } finally {
      active.current.delete(job.id); emit(); pump();
    }
  }
  function pump() {
    if (!alive.current || running.current) return;
    running.current = true;
    for (const job of jobs.current) {
      if (active.current.size >= 2) break;
      if (job.status === 'queued' && !active.current.has(job.id)) work(job);
    }
    running.current = false;
  }
  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    (async () => {
      const share = publicToken || new URLSearchParams(window.location.hash.slice(1)).get('share') || '';
      const ctx = credentials(share);
      if (!ctx.auth && !ctx.share) return;
      try {
        const scope = await scopeFor(ctx), saved = await store('all');
        if (cancelled) return;
        const existing = new Set(jobs.current.map(j => j.id));
        jobs.current.push(...saved.filter(j => j.scope === scope && !existing.has(j.id)).map(j => ({...j,ctx,status:'paused',error:'Продолжите сохранённую загрузку'})));
        emit();
      } catch { /* Об ограничении сохранения сообщаем при реальной загрузке. */ }
    })();
    const beforeUnload = e => { if (jobs.current.some(j => ['uploading','queued'].includes(j.status))) {e.preventDefault(); e.returnValue='';} };
    window.addEventListener('beforeunload',beforeUnload);
    return () => {cancelled=true;alive.current=false;window.removeEventListener('beforeunload',beforeUnload);jobs.current.forEach(j=>j.controller?.abort());};
  }, [publicToken]);
  async function enqueue(files, folder, ctx, target = null) {
    const list = Array.from(files);
    if (!list.length) return;
    folder = typeof folder === 'string' ? folder.trim() : '';
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(folder)) {
      throw new Error('Откройте папку для загрузки файлов');
    }
    if (list.some(f => !f.size || f.size > SIZE_LIMIT)) throw new Error('Каждый файл должен быть от 1 байта до 2 ГиБ');
    if (jobs.current.filter(j => j.status !== 'done').length + list.length > 100) throw new Error('В очереди может быть не более 100 файлов');
    const scope = await scopeFor(ctx);
    for (const file of list) {
      const job = {id:uuid(),file,folder,ctx,scope,target,status:'queued',progress:0,error:''};
      jobs.current.push(job); await persist(job);
    }
    setHidden(false); setExpanded(true); emit(); pump();
  }
  async function remove(job) {
    if (active.current.has(job.id)) {job.controller?.abort();return;}
    if (job.uploadId && job.status !== 'done') {
      try {await request(`uploads/${job.uploadId}/`,{method:'DELETE',ctx:job.ctx});} catch { /* Сервер очистит резерв через 48 часов. */ }
    }
    jobs.current = jobs.current.filter(j=>j.id!==job.id); await store('delete',job.id).catch(()=>{}); emit();
  }
  function dismissPanel() {
    // Убираем завершённые строки, сохраняем незавершённую очередь и её управление.
    jobs.current = jobs.current.filter(j => j.status !== 'done');
    setHidden(true); emit();
  }
  const busy = items.filter(j=>['uploading','queued'].includes(j.status)).length;
  return <QueueContext.Provider value={{enqueue}}>{children}{items.length > 0 && createPortal(
    hidden ? <button className="st-upload-reopen" onClick={() => {setHidden(false); setExpanded(true);}}
      aria-label="Показать очередь загрузки">↑ Загрузки · {busy || items.length}</button> : <aside className="st-upload" aria-label="Очередь загрузки">
      <div className="st-upload-header"><button className="st-upload-head" onClick={()=>setExpanded(v=>!v)}><strong>↑ Загрузки · {busy ? `в работе ${busy}` : 'очередь'}</strong><span>{expanded?'Свернуть':'Развернуть'}</span></button><button type="button" className="st-upload-close" aria-label="Скрыть панель загрузок" title="Скрыть панель" onClick={dismissPanel}>×</button></div>
      {expanded && <div className="st-upload-body">{warning && <p role="status" className="st-warning">{warning}</p>}
        <p className="st-muted">Можно переходить между разделами. Не закрывайте вкладку до завершения.</p>
        {items.map(job=><div className="st-job" key={job.id}><strong title={job.file.name}>{job.file.name}</strong><small>{bytes(job.file.size)} · {job.status==='done'?'Готово':job.preparing != null ? `Подготовка ${job.preparing}%` : `${job.progress}%`}</small>
          <progress max="100" value={job.progress}/>{job.error && <small role="status">{job.error}</small>}
          <div className="st-row">{['paused','error'].includes(job.status)&&<button onClick={()=>{const j=jobs.current.find(x=>x.id===job.id);if(j){j.status='queued';pump();emit();}}}>Продолжить</button>}
          {job.status==='uploading'?<button onClick={()=>job.controller?.abort()}>Пауза</button>:<button onClick={()=>remove(job)}>{job.status==='done'?'Убрать':'Отменить'}</button>}</div></div>)}
      </div>}
    </aside>, document.body)}</QueueContext.Provider>;
}